import {
  el,
  formatBytes,
  formatEta,
  formatRate,
  sanitizeFilename,
} from "./dom";
import type { Completed, Progress } from "../transfer/session";

interface Card {
  root: HTMLElement;
  pct: HTMLElement;
  fill: HTMLElement;
  knob: HTMLElement;
  bytes: HTMLElement;
  rate: HTMLElement;
  lastBytes: number;
  lastT: number;
  ema: number;
  startT: number;
}

/**
 * Renders one progress card per transfer. Each card shows only THIS peer's own
 * counters for that transfer — a slider-style bar, percentage, bytes, rate, ETA.
 */
export class ProgressList {
  private readonly cards = new Map<string, Card>();

  constructor(private readonly container: HTMLElement) {}

  update(p: Progress): void {
    const card = this.cards.get(p.id) ?? this.create(p);
    const pct = p.size ? Math.min(100, (p.transferred / p.size) * 100) : 0;
    card.pct.textContent = `${pct.toFixed(pct >= 100 ? 0 : 1)}%`;
    card.fill.style.width = `${pct}%`;
    card.knob.style.left = `${pct}%`;
    card.bytes.textContent = `${formatBytes(p.transferred)} / ${formatBytes(p.size)}`;

    const now = performance.now();
    const dt = (now - card.lastT) / 1000;
    if (dt >= 0.4) {
      const inst = (p.transferred - card.lastBytes) / dt;
      card.ema = card.ema ? card.ema * 0.7 + inst * 0.3 : inst;
      card.lastBytes = p.transferred;
      card.lastT = now;
      const remain =
        card.ema > 0 ? (p.size - p.transferred) / card.ema : Infinity;
      card.rate.textContent =
        p.transferred < p.size
          ? `${formatRate(card.ema)} \u00b7 ${formatEta(remain)}`
          : formatRate(card.ema);
    }
  }

  complete(c: Completed): void {
    const card = this.cards.get(c.id);
    if (!card) return;
    card.pct.textContent = "100%";
    card.fill.style.width = "100%";
    card.knob.style.left = "100%";
    card.bytes.textContent = `${formatBytes(c.size)} / ${formatBytes(c.size)}`;
    card.root.classList.add("done");
    const avg =
      c.size / Math.max(0.001, (performance.now() - card.startT) / 1000);
    card.rate.textContent = `${formatRate(avg)} avg`;
  }

  private create(p: Progress): Card {
    const fill = el("div", { class: "fill" });
    const knob = el("div", { class: "knob" });
    const pct = el("div", { class: "pct", text: "0%" });
    const bytes = el("div", { class: "b", text: "0 B" });
    const rate = el("div", { class: "b muted", text: "" });
    const root = el(
      "div",
      { class: "transfer" },
      el(
        "div",
        { class: "transfer-head" },
        el("span", {
          class: `tag tag-${p.direction}`,
          text: p.direction === "send" ? "Sending" : "Receiving",
        }),
        el("span", { class: "fname", text: sanitizeFilename(p.name) })
      ),
      pct,
      el("div", { class: "track" }, fill, knob),
      el("div", { class: "stats" }, bytes, rate)
    );
    this.container.prepend(root);

    const now = performance.now();
    const card: Card = {
      root,
      pct,
      fill,
      knob,
      bytes,
      rate,
      lastBytes: 0,
      lastT: now,
      ema: 0,
      startT: now,
    };
    this.cards.set(p.id, card);
    return card;
  }
}
